/**
 * Permission Service
 * 
 * Handles feature access control, rate limiting, and permission management
 * for the Propeller Travel platform.
 */

import { supabase } from '../lib/supabase';

type JsonPrimitive = string | number | boolean | null;
type JsonValue = JsonPrimitive | JsonObject | JsonValue[];

interface JsonObject {
  [key: string]: JsonValue | undefined;
}

// Types
export interface UserType {
  id: string;
  name: string;
  display_name: string;
  description: string | null;
  priority: number;
  is_system: boolean;
  is_active: boolean;
  color: string;
  icon: string;
  created_at: string;
  updated_at: string;
}

export interface FeatureDefinition {
  id: string;
  feature_key: string;
  display_name: string;
  description: string | null;
  category: string;
  default_config: JsonObject;
  is_active: boolean;
  sort_order: number;
}

export interface FeaturePermission {
  id: string;
  user_type_id: string;
  feature_key: string;
  can_access: boolean;
  config: JsonObject;
}

export interface UserPermissionOverride {
  id: string;
  user_id: string;
  feature_key: string;
  can_access: boolean | null;
  config: JsonObject;
  reason: string | null;
  granted_by: string | null;
  expires_at: string | null;
}

export interface RateLimit {
  id: string;
  user_type_id: string;
  action_type: string;
  max_requests: number;
  window_seconds: number;
  burst_limit: number | null;
  burst_window_seconds: number;
  is_active: boolean;
}

export interface RateLimitResult {
  allowed: boolean;
  remaining: number;
  reset_at: string | null;
  limit?: number;
}

export interface EffectivePermission {
  feature_key: string;
  can_access: boolean;
  config: JsonObject;
  source: 'user_type' | 'override';
}

export interface PlatformSetting {
  id: string;
  setting_key: string;
  setting_value: JsonValue;
  display_name: string;
  description: string | null;
  category: string;
  data_type: string;
  validation_rules: JsonObject;
  is_sensitive: boolean;
  is_editable: boolean;
}

// Cache for permissions (short TTL)
const permissionCache = new Map<string, { data: EffectivePermission[]; timestamp: number }>();
const CACHE_TTL_MS = 60000; // 1 minute

/**
 * Clear permission cache for a user
 */
export function clearPermissionCache(userId?: string): void {
  if (userId) {
    permissionCache.delete(userId);
  } else {
    permissionCache.clear();
  }
}

/**
 * Get all user types
 */
export async function getUserTypes(): Promise<UserType[]> {
  const { data, error } = await supabase
    .from('user_types')
    .select('*')
    .eq('is_active', true)
    .order('priority', { ascending: false });

  if (error) {
    console.error('Error fetching user types:', error);
    throw error;
  }

  return data || [];
}

/**
 * Get a single user type by name
 */
export async function getUserTypeByName(name: string): Promise<UserType | null> {
  const { data, error } = await supabase
    .from('user_types')
    .select('*')
    .eq('name', name)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null; // Not found
    console.error('Error fetching user type:', error);
    throw error;
  }

  return data;
}

/**
 * Get all feature definitions
 */
export async function getFeatureDefinitions(): Promise<FeatureDefinition[]> {
  const { data, error } = await supabase
    .from('feature_definitions')
    .select('*')
    .eq('is_active', true)
    .order('sort_order', { ascending: true });

  if (error) {
    console.error('Error fetching feature definitions:', error);
    throw error;
  }

  return data || [];
}

/**
 * Get feature definitions grouped by category
 */
export async function getFeatureDefinitionsByCategory(): Promise<Record<string, FeatureDefinition[]>> {
  const features = await getFeatureDefinitions();
  return features.reduce((acc, feature) => {
    if (!acc[feature.category]) {
      acc[feature.category] = [];
    }
    acc[feature.category].push(feature);
    return acc;
  }, {} as Record<string, FeatureDefinition[]>);
}

/**
 * Get effective permissions for a user (merges user type + overrides)
 */
export async function getEffectivePermissions(userId: string): Promise<EffectivePermission[]> {
  // Check cache
  const cached = permissionCache.get(userId);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
    return cached.data;
  }

  const { data, error } = await supabase
    .rpc('get_user_effective_permissions', { p_user_id: userId });

  if (error) {
    console.error('Error fetching effective permissions:', error);
    throw error;
  }

  const permissions = (data || []) as EffectivePermission[];
  
  // Cache the result
  permissionCache.set(userId, { data: permissions, timestamp: Date.now() });

  return permissions;
}

/**
 * Check if user has access to a specific feature
 */
export async function checkFeatureAccess(
  userId: string,
  featureKey: string
): Promise<{ hasAccess: boolean; config: JsonObject }> {
  const permissions = await getEffectivePermissions(userId);
  const permission = permissions.find(p => p.feature_key === featureKey);

  if (!permission) {
    return { hasAccess: false, config: {} };
  }

  return {
    hasAccess: permission.can_access,
    config: permission.config || {},
  };
}

/**
 * Check if user has access to multiple features
 */
export async function checkMultipleFeatureAccess(
  userId: string,
  featureKeys: string[]
): Promise<Record<string, { hasAccess: boolean; config: JsonObject }>> {
  const permissions = await getEffectivePermissions(userId);
  
  return featureKeys.reduce((acc, key) => {
    const permission = permissions.find(p => p.feature_key === key);
    acc[key] = {
      hasAccess: permission?.can_access ?? false,
      config: permission?.config || {},
    };
    return acc;
  }, {} as Record<string, { hasAccess: boolean; config: JsonObject }>);
}

/**
 * Check rate limit for an action
 */
export async function checkRateLimit(
  userId: string,
  actionType: string
): Promise<RateLimitResult> {
  const { data, error } = await supabase
    .rpc('check_rate_limit', { p_user_id: userId, p_action_type: actionType });

  if (error) {
    console.error('Error checking rate limit:', error);
    // Fail open - allow the request but log the error
    return { allowed: true, remaining: -1, reset_at: null };
  }

  return data as RateLimitResult;
}

/**
 * Get user's current user type
 */
export async function getUserUserType(userId: string): Promise<UserType | null> {
  const { data, error } = await supabase
    .from('user_profiles')
    .select('user_type_id, user_types(*)')
    .eq('user_id', userId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    console.error('Error fetching user type:', error);
    throw error;
  }

  const linkedUserType = data?.user_types;
  return (Array.isArray(linkedUserType) ? linkedUserType[0] : linkedUserType) as UserType | null;
}

/**
 * Set user's user type
 */
export async function setUserUserType(userId: string, userTypeId: string): Promise<void> {
  const { error } = await supabase
    .from('user_profiles')
    .update({ user_type_id: userTypeId })
    .eq('user_id', userId);

  if (error) {
    console.error('Error setting user type:', error);
    throw error;
  }

  clearPermissionCache(userId);
}

/**
 * Get permission overrides for a user
 */
export async function getUserPermissionOverrides(userId: string): Promise<UserPermissionOverride[]> {
  const { data, error } = await supabase
    .from('user_permission_overrides')
    .select('*')
    .eq('user_id', userId);

  if (error) {
    console.error('Error fetching permission overrides:', error);
    throw error;
  }

  return data || [];
}

/**
 * Set a permission override for a user
 */
export async function setPermissionOverride(
  userId: string,
  featureKey: string,
  canAccess: boolean | null,
  config: JsonObject = {},
  options: {
    reason?: string;
    expiresAt?: string;
  } = {}
): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  const priorOverride = await fetchPriorOverride(userId, featureKey);

  const { error } = await supabase
    .from('user_permission_overrides')
    .upsert({
      user_id: userId,
      feature_key: featureKey,
      can_access: canAccess,
      config,
      reason: options.reason,
      expires_at: options.expiresAt,
      granted_by: user?.id,
    }, {
      onConflict: 'user_id,feature_key',
    });

  if (error) {
    console.error('Error setting permission override:', error);
    throw error;
  }

  clearPermissionCache(userId);

  // Log to audit
  await logPermissionChange('modify', 'user_override', null, userId, {
    feature_key: featureKey,
    can_access: canAccess,
    config,
    reason: options.reason,
  }, { oldValue: priorOverride, targetLabel: featureKey, reason: options.reason ?? null });
}

/**
 * Remove a permission override
 */
export async function removePermissionOverride(userId: string, featureKey: string): Promise<void> {
  const priorOverride = await fetchPriorOverride(userId, featureKey);
  const { error } = await supabase
    .from('user_permission_overrides')
    .delete()
    .eq('user_id', userId)
    .eq('feature_key', featureKey);

  if (error) {
    console.error('Error removing permission override:', error);
    throw error;
  }

  clearPermissionCache(userId);

  await logPermissionChange('revoke', 'user_override', null, userId, { feature_key: featureKey }, {
    oldValue: priorOverride,
    targetLabel: featureKey,
  });
}

/**
 * Get all feature permissions for a user type
 */
export async function getFeaturePermissionsForUserType(userTypeId: string): Promise<FeaturePermission[]> {
  const { data, error } = await supabase
    .from('feature_permissions')
    .select('*')
    .eq('user_type_id', userTypeId);

  if (error) {
    console.error('Error fetching feature permissions:', error);
    throw error;
  }

  return data || [];
}

/**
 * Update feature permission for a user type
 */
export async function updateFeaturePermission(
  userTypeId: string,
  featureKey: string,
  canAccess: boolean,
  config: JsonObject = {}
): Promise<void> {
  const priorFeature = await fetchPriorFeaturePermission(userTypeId, featureKey);
  const { error } = await supabase
    .from('feature_permissions')
    .upsert({
      user_type_id: userTypeId,
      feature_key: featureKey,
      can_access: canAccess,
      config,
    }, {
      onConflict: 'user_type_id,feature_key',
    });

  if (error) {
    console.error('Error updating feature permission:', error);
    throw error;
  }

  // Clear all caches since this affects multiple users
  clearPermissionCache();

  await logPermissionChange('modify', 'feature_permission', userTypeId, null, {
    feature_key: featureKey,
    can_access: canAccess,
    config,
  }, { oldValue: priorFeature, targetLabel: await fetchUserTypeLabel(userTypeId) });
}

/**
 * Get rate limits for a user type
 */
export async function getRateLimitsForUserType(userTypeId: string): Promise<RateLimit[]> {
  const { data, error } = await supabase
    .from('rate_limits')
    .select('*')
    .eq('user_type_id', userTypeId);

  if (error) {
    console.error('Error fetching rate limits:', error);
    throw error;
  }

  return data || [];
}

/**
 * Update rate limit for a user type
 */
export async function updateRateLimit(
  userTypeId: string,
  actionType: string,
  maxRequests: number,
  windowSeconds: number,
  options: {
    burstLimit?: number;
    burstWindowSeconds?: number;
    isActive?: boolean;
  } = {}
): Promise<void> {
  const priorRateLimit = await fetchPriorRateLimit(userTypeId, actionType);
  const { error } = await supabase
    .from('rate_limits')
    .upsert({
      user_type_id: userTypeId,
      action_type: actionType,
      max_requests: maxRequests,
      window_seconds: windowSeconds,
      burst_limit: options.burstLimit,
      burst_window_seconds: options.burstWindowSeconds ?? 10,
      is_active: options.isActive ?? true,
    }, {
      onConflict: 'user_type_id,action_type',
    });

  if (error) {
    console.error('Error updating rate limit:', error);
    throw error;
  }

  await logPermissionChange('modify', 'rate_limit', userTypeId, null, {
    action_type: actionType,
    max_requests: maxRequests,
    window_seconds: windowSeconds,
    ...options,
  }, { oldValue: priorRateLimit, targetLabel: await fetchUserTypeLabel(userTypeId) });
}

/**
 * Get platform settings
 */
export async function getPlatformSettings(category?: string): Promise<PlatformSetting[]> {
  let query = supabase
    .from('platform_settings')
    .select('*')
    .order('category', { ascending: true });

  if (category) {
    query = query.eq('category', category);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching platform settings:', error);
    throw error;
  }

  return data || [];
}

/**
 * Get a single platform setting
 */
export async function getPlatformSetting<T = JsonValue>(key: string): Promise<T | null> {
  const { data, error } = await supabase
    .from('platform_settings')
    .select('setting_value')
    .eq('setting_key', key)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    console.error('Error fetching platform setting:', error);
    throw error;
  }

  return data?.setting_value as T;
}

/**
 * Update a platform setting
 */
export async function updatePlatformSetting(key: string, value: JsonValue): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  const priorSetting = await fetchPriorSettingValue(key);

  const { error } = await supabase
    .from('platform_settings')
    .update({ 
      setting_value: value,
      updated_by: user?.id,
    })
    .eq('setting_key', key);

  if (error) {
    console.error('Error updating platform setting:', error);
    throw error;
  }

  await logPermissionChange('modify', 'setting', null, null, { key, value }, {
    oldValue: priorSetting === undefined ? null : { key, value: priorSetting },
    targetLabel: key,
  });
}

/**
 * Validate an invite code
 */
export async function validateInviteCode(code: string): Promise<{
  valid: boolean;
  userTypeId?: string;
  metadata?: JsonObject;
  error?: string;
}> {
  const { data, error } = await supabase
    .rpc('validate_invite_code', { p_code: code });

  if (error) {
    console.error('Error validating invite code:', error);
    return { valid: false, error: 'Failed to validate code' };
  }

  return {
    valid: data.valid,
    userTypeId: data.user_type_id,
    metadata: data.metadata,
    error: data.error,
  };
}

/**
 * Create a new invite code
 */
export async function createInviteCode(options: {
  userTypeId?: string;
  maxUses?: number;
  expiresAt?: string;
  metadata?: JsonObject;
}): Promise<string> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  // Generate a random code
  const code = generateInviteCode();

  const { error } = await supabase
    .from('invite_codes')
    .insert({
      code,
      created_by: user.id,
      assigned_user_type_id: options.userTypeId,
      max_uses: options.maxUses ?? 1,
      expires_at: options.expiresAt,
      metadata: options.metadata ?? {},
    });

  if (error) {
    console.error('Error creating invite code:', error);
    throw error;
  }

  return code;
}

/**
 * Generate a random invite code
 */
function generateInviteCode(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < 8; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
}

/**
 * Log permission changes for audit
 */
export interface PermissionChangeContext {
  /** The row as it was before the write (null when there was none, or it could not be read). */
  oldValue?: JsonValue | null;
  /** A readable name for the target, recorded so the log still reads after the target is renamed or deleted. */
  targetLabel?: string | null;
  reason?: string | null;
}

/** Builds the permission_audit_log row (Astra PTUX-160): old value, role/target label and reason are recorded. */
export function buildPermissionAuditRow(
  actorId: string | undefined,
  action: string,
  targetType: string,
  targetId: string | null,
  targetUserId: string | null,
  newValue: JsonValue,
  context: PermissionChangeContext = {}
) {
  const label = context.targetLabel ?? null;
  const labelledNew: JsonValue =
    label && newValue !== null && typeof newValue === 'object' && !Array.isArray(newValue)
      ? { ...newValue, target_label: label }
      : newValue;
  return {
    actor_id: actorId,
    action,
    target_type: targetType,
    target_id: targetId,
    target_user_id: targetUserId,
    old_value: context.oldValue ?? null,
    new_value: labelledNew,
    ...(context.reason ? { reason: context.reason } : {}),
  };
}

async function fetchPriorOverride(userId: string, featureKey: string): Promise<JsonValue | null> {
  try {
    const { data } = await supabase
      .from('user_permission_overrides')
      .select('feature_key, can_access, config, reason, expires_at')
      .eq('user_id', userId)
      .eq('feature_key', featureKey)
      .maybeSingle();
    return (data as JsonValue | null) ?? null;
  } catch {
    return null;
  }
}

async function fetchPriorFeaturePermission(userTypeId: string, featureKey: string): Promise<JsonValue | null> {
  try {
    const { data } = await supabase
      .from('feature_permissions')
      .select('feature_key, can_access, config')
      .eq('user_type_id', userTypeId)
      .eq('feature_key', featureKey)
      .maybeSingle();
    return (data as JsonValue | null) ?? null;
  } catch {
    return null;
  }
}

async function fetchPriorRateLimit(userTypeId: string, actionType: string): Promise<JsonValue | null> {
  try {
    const { data } = await supabase
      .from('rate_limits')
      .select('action_type, max_requests, window_seconds, burst_limit, burst_window_seconds, is_active')
      .eq('user_type_id', userTypeId)
      .eq('action_type', actionType)
      .maybeSingle();
    return (data as JsonValue | null) ?? null;
  } catch {
    return null;
  }
}

async function fetchPriorSettingValue(key: string): Promise<JsonValue | undefined> {
  try {
    const { data } = await supabase
      .from('platform_settings')
      .select('setting_value')
      .eq('setting_key', key)
      .maybeSingle();
    return data ? ((data as { setting_value: JsonValue }).setting_value ?? null) : undefined;
  } catch {
    return undefined;
  }
}

/** The role name for a user_types id, recorded on the audit row so it survives a rename. */
async function fetchUserTypeLabel(userTypeId: string): Promise<string | null> {
  try {
    const { data } = await supabase
      .from('user_types')
      .select('name, display_name')
      .eq('id', userTypeId)
      .maybeSingle();
    const row = data as { name?: string; display_name?: string | null } | null;
    return row ? (row.display_name || row.name || null) : null;
  } catch {
    return null;
  }
}

async function logPermissionChange(
  action: string,
  targetType: string,
  targetId: string | null,
  targetUserId: string | null,
  newValue: JsonValue,
  context: PermissionChangeContext = {}
): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();

  // actor_id is written first, in the signed-in user's own name, as the row rule for this table requires.
  const { actor_id: _actorId, ...auditFields } = buildPermissionAuditRow(user?.id, action, targetType, targetId, targetUserId, newValue, context);

  try {
    await supabase
      .from('permission_audit_log')
      .insert({
        actor_id: user?.id,
        ...auditFields,
      });
  } catch (error) {
    // Don't throw on audit log failure
    console.error('Failed to log permission change:', error);
  }
}

/**
 * Get pending user approvals
 */
export async function getPendingApprovals(): Promise<Array<Record<string, unknown>>> {
  const { data, error } = await supabase
    .from('user_profiles')
    .select('*, user_types(name, display_name)')
    .eq('approval_status', 'pending')
    .order('created_at', { ascending: true });

  if (error) {
    console.error('Error fetching pending approvals:', error);
    throw error;
  }

  return data || [];
}

/**
 * Approve a user registration
 */
export async function approveUser(userId: string, userTypeId?: string): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const updateData: {
    approval_status: string;
    approved_by: string;
    approved_at: string;
    user_type_id?: string;
  } = {
    approval_status: 'approved',
    approved_by: user.id,
    approved_at: new Date().toISOString(),
  };

  if (userTypeId) {
    updateData.user_type_id = userTypeId;
  }

  const { error } = await supabase
    .from('user_profiles')
    .update(updateData)
    .eq('user_id', userId);

  if (error) {
    console.error('Error approving user:', error);
    throw error;
  }

  clearPermissionCache(userId);
}

/**
 * Reject a user registration
 */
export async function rejectUser(userId: string, reason?: string): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { error } = await supabase
    .from('user_profiles')
    .update({
      approval_status: 'rejected',
      rejection_reason: reason,
      approved_by: user.id,
      approved_at: new Date().toISOString(),
    })
    .eq('user_id', userId);

  if (error) {
    console.error('Error rejecting user:', error);
    throw error;
  }
}

/**
 * Suspend a user
 */
export async function suspendUser(userId: string, reason?: string): Promise<void> {
  const { error } = await supabase
    .from('user_profiles')
    .update({
      approval_status: 'suspended',
      rejection_reason: reason,
    })
    .eq('user_id', userId);

  if (error) {
    console.error('Error suspending user:', error);
    throw error;
  }

  clearPermissionCache(userId);
}

/**
 * Check if user is approved
 */
export async function isUserApproved(userId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from('user_profiles')
    .select('approval_status, role')
    .eq('user_id', userId)
    .single();

  if (error) {
    console.error('Error checking user approval:', error);
    return false;
  }

  // Admins and super_admins are always considered approved
  if (data?.role === 'admin' || data?.role === 'super_admin') {
    return true;
  }

  return data?.approval_status === 'approved';
}

/**
 * Get permission audit log
 */
export async function getPermissionAuditLog(options: {
  limit?: number;
  offset?: number;
  actorId?: string;
  targetUserId?: string;
  action?: string;
}): Promise<Array<Record<string, unknown>>> {
  let query = supabase
    .from('permission_audit_log')
    .select('*')
    .order('created_at', { ascending: false });

  if (options.actorId) {
    query = query.eq('actor_id', options.actorId);
  }
  if (options.targetUserId) {
    query = query.eq('target_user_id', options.targetUserId);
  }
  if (options.action) {
    query = query.eq('action', options.action);
  }
  if (options.limit) {
    query = query.limit(options.limit);
  }
  if (options.offset) {
    query = query.range(options.offset, options.offset + (options.limit || 50) - 1);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching audit log:', error);
    throw error;
  }

  return data || [];
}

export interface TeamPermissionMatrixMember {
  user_id: string;
  email: string;
  full_name: string | null;
  role: string;
  approval_status: string;
  user_type_id: string | null;
  user_type_name: string | null;
  user_type_display: string | null;
  user_type_color: string | null;
}

export interface TeamPermissionMatrixFeature {
  feature_key: string;
  display_name: string;
  description: string | null;
  category: string;
  sort_order: number;
  role_defaults: Record<string, boolean>;
}

export interface TeamPermissionMatrixOverride {
  id: string;
  user_id: string;
  feature_key: string;
  can_access: boolean | null;
  reason: string | null;
  expires_at: string | null;
  updated_at: string | null;
}

export interface TeamPermissionMatrixPayload {
  staff: TeamPermissionMatrixMember[];
  features: TeamPermissionMatrixFeature[];
  overrides: TeamPermissionMatrixOverride[];
}

export type TeamMemberPermissionMode = 'allow' | 'deny' | 'default' | 'inherit';

/**
 * Load the full team permission matrix (approved staff, active features with role defaults, and active user overrides).
 */
export async function getTeamPermissionMatrix(): Promise<TeamPermissionMatrixPayload> {
  const { data, error } = await supabase.rpc('team_permission_matrix_get_v1');

  if (error) {
    console.error('Error loading team permission matrix:', error);
    throw error;
  }

  const payload = (data || {}) as Record<string, unknown>;
  const rawStaff = (
    Array.isArray(payload.staff)
      ? payload.staff
      : Array.isArray(payload.staff_members)
        ? payload.staff_members
        : []
  ) as Array<Record<string, unknown>>;
  const rawFeatures = (Array.isArray(payload.features) ? payload.features : []) as Array<
    Record<string, unknown>
  >;
  const rawRoleDefaults = (
    Array.isArray(payload.role_defaults) ? payload.role_defaults : []
  ) as Array<Record<string, unknown>>;
  const rawOverrides = Array.isArray(payload.overrides)
    ? payload.overrides
    : Array.isArray(payload.user_overrides)
      ? payload.user_overrides
      : [];

  const typeNameById = new Map<string, string>();
  for (const member of rawStaff) {
    const typeId = typeof member.user_type_id === 'string' ? member.user_type_id : '';
    const typeName =
      typeof member.user_type_name === 'string' && member.user_type_name
        ? member.user_type_name
        : typeof member.role === 'string'
          ? member.role
          : '';
    if (typeId && typeName) {
      typeNameById.set(typeId, typeName);
    }
  }

  const defaultsByFeature = new Map<string, Record<string, boolean>>();
  for (const row of rawRoleDefaults) {
    const featureKey = typeof row.feature_key === 'string' ? row.feature_key : '';
    if (!featureKey) continue;
    const canAccess = Boolean(row.can_access);
    const map = defaultsByFeature.get(featureKey) ?? {};
    const typeId = typeof row.user_type_id === 'string' ? row.user_type_id : '';
    const typeName =
      typeof row.user_type_name === 'string' && row.user_type_name
        ? row.user_type_name
        : typeId
          ? typeNameById.get(typeId) ?? ''
          : '';
    if (typeId) map[typeId] = canAccess;
    if (typeName) map[typeName] = canAccess;
    defaultsByFeature.set(featureKey, map);
  }

  const normalizedFeatures: TeamPermissionMatrixFeature[] = rawFeatures.map((f) => {
    const featureKey = String(f.feature_key ?? '');
    const existingDefaults =
      f.role_defaults && typeof f.role_defaults === 'object'
        ? (f.role_defaults as Record<string, boolean>)
        : {};
    const rpcDefaults = defaultsByFeature.get(featureKey) ?? {};
    return {
      feature_key: featureKey,
      display_name: String(f.display_name ?? featureKey),
      description: (f.description ?? null) as string | null,
      category: String(f.category ?? 'general'),
      sort_order: Number(f.sort_order ?? 100),
      role_defaults: {
        ...existingDefaults,
        ...rpcDefaults,
      },
    };
  });

  return {
    staff: rawStaff as unknown as TeamPermissionMatrixMember[],
    features: normalizedFeatures,
    overrides: rawOverrides as TeamPermissionMatrixOverride[],
  };
}

/**
 * Set or clear a per-team-member permission override ('allow', 'deny', or 'default'/'inherit') with audit trail.
 */
export async function setTeamMemberPermission(
  targetUserId: string,
  featureKey: string,
  mode: TeamMemberPermissionMode,
  reason?: string
): Promise<Record<string, unknown>> {
  const rpcMode = mode === 'default' ? 'inherit' : mode;
  const { data, error } = await supabase.rpc('team_member_permission_set_v1', {
    p_target_user_id: targetUserId,
    p_feature_key: featureKey,
    p_mode: rpcMode,
    p_reason: reason ?? null,
  });

  if (error) {
    console.error('Error setting team member permission:', error);
    throw error;
  }

  clearPermissionCache(targetUserId);
  return (data || {}) as Record<string, unknown>;
}

// Export a default instance for convenience
export default {
  getUserTypes,
  getUserTypeByName,
  getFeatureDefinitions,
  getFeatureDefinitionsByCategory,
  getEffectivePermissions,
  checkFeatureAccess,
  checkMultipleFeatureAccess,
  checkRateLimit,
  getUserUserType,
  setUserUserType,
  getUserPermissionOverrides,
  setPermissionOverride,
  removePermissionOverride,
  getFeaturePermissionsForUserType,
  updateFeaturePermission,
  getRateLimitsForUserType,
  updateRateLimit,
  getPlatformSettings,
  getPlatformSetting,
  updatePlatformSetting,
  validateInviteCode,
  createInviteCode,
  getPendingApprovals,
  approveUser,
  rejectUser,
  suspendUser,
  isUserApproved,
  getPermissionAuditLog,
  getTeamPermissionMatrix,
  setTeamMemberPermission,
  clearPermissionCache,
};




